import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import bgimg from "../../assets/login.png";
import logo from "../../assets/Easymarg Whitebg.png";
import { FaUser, FaLock, FaEye, FaEyeSlash, FaShieldAlt, FaTimes, FaRedo } from "react-icons/fa";
import axiosInstance from "../../api/axiosInstance";
import { GoogleLogin } from "@react-oauth/google";

const Login = () => {
    const navigate = useNavigate();

    // States
    const [email, setEmail] = useState(localStorage.getItem("rememberedUserEmail") || "");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [rememberMe, setRememberMe] = useState(!!localStorage.getItem("rememberedUserEmail"));

    // Google Login OTP Verification States
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [pendingGoogleToken, setPendingGoogleToken] = useState("");
    const [pendingGoogleEmail, setPendingGoogleEmail] = useState("");

    // Auto-redirect logic with safety check
    useEffect(() => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (token) {
            navigate("/home", { replace: true });
        }
    }, [navigate]);

    // Countdown timer effect for OTP modal
    useEffect(() => {
        let timer;
        if (showOtpModal && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else if (countdown === 0) {
            setCanResend(true);
        }
        return () => clearInterval(timer);
    }, [showOtpModal, countdown]);

    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    // --- Google Login Logic ---
    const handleGoogleSuccess = async (credentialResponse) => {
        setLoading(true);
        setError("");
        try {
            const response = await axiosInstance.post("/users/google-login", {
                tokenId: credentialResponse.credential
            });

            if (response.data.requireOtp) {
                setPendingGoogleToken(credentialResponse.credential);
                setPendingGoogleEmail(response.data.email);
                setOtp("");
                setError("");
                setCountdown(60);
                setCanResend(false);
                setShowOtpModal(true);
                return;
            }

            const { token, user } = response.data;
            const displayName = user.username || "User Account";

            localStorage.setItem('token', token);
            localStorage.setItem("userId", user.id);
            localStorage.setItem("userName", displayName);
            if (user.profilePic) localStorage.setItem("userPic", user.profilePic);

            window.dispatchEvent(new Event("authChange"));
            navigate("/home", { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || "Google authentication failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyGoogleOtp = async (e) => {
        e.preventDefault();
        if (!otp.trim()) {
            return setError("Please enter the 6-digit verification code");
        }
        if (otp.trim().length !== 6) {
            return setError("Verification code must be 6 digits");
        }

        setOtpLoading(true);
        setError("");
        try {
            const response = await axiosInstance.post("/users/google-login", {
                tokenId: pendingGoogleToken,
                otp: otp.trim()
            });

            const { token, user } = response.data;
            const displayName = user.username || "User Account";

            localStorage.setItem('token', token);
            localStorage.setItem("userId", user.id);
            localStorage.setItem("userName", displayName);
            if (user.profilePic) localStorage.setItem("userPic", user.profilePic);

            setShowOtpModal(false);
            window.dispatchEvent(new Event("authChange"));
            navigate("/home", { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || "Invalid or expired verification code.");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendGoogleOtp = async () => {
        if (!canResend || resendLoading) return;
        setResendLoading(true);
        try {
            await axiosInstance.post("/users/google-login", {
                tokenId: pendingGoogleToken
            });
            setCountdown(60);
            setCanResend(false);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to resend code");
        } finally {
            setResendLoading(false);
        }
    };

    // --- Manual Login Logic ---
    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setSuccessMessage("");

        if (!email.trim() || !password.trim()) {
            setError("Please fill in all fields.");
            return;
        }

        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }

        setLoading(true);

        try {
            const response = await axiosInstance.post("/users/login", {
                emailid: email,
                password,
                rememberMe 
            });

            const storage = rememberMe ? localStorage : sessionStorage;
            const displayName = response.data.user.username || response.data.user.name || "User Account";

            storage.setItem('token', response.data.token);
            storage.setItem("userId", response.data.user.id);
            storage.setItem("userName", displayName);

            if (!rememberMe) {
                localStorage.setItem("userName", displayName);
                localStorage.setItem("userId", response.data.user.id);
            }

            if (rememberMe) {
                localStorage.setItem("rememberedUserEmail", email);
            } else {
                localStorage.removeItem("rememberedUserEmail");
            }

            window.dispatchEvent(new Event("authChange"));
            navigate("/home", { replace: true });

        } catch (err) {
            setError(err.response?.data?.message || "Invalid credentials.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-screen w-full flex flex-col lg:flex-row overflow-hidden">

            {/* Left Brand Panel */}
            <div
                className="hidden lg:block relative w-full lg:w-1/2 lg:h-full shrink-0 bg-cover bg-center overflow-hidden"
                style={{ backgroundImage: `url(${bgimg})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a2463]/95 via-[#134DA7]/90 to-[#0a2463]/95 backdrop-blur-sm"></div>

                <div className="absolute top-5 left-5 sm:top-8 sm:left-8 z-20 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2.5 shadow-lg">
                    <img src={logo} alt="EasyMarg Logo" className="h-6 sm:h-7" />
                </div>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={staggerContainer}
                    className="relative z-10 h-full flex flex-col items-center lg:items-start justify-center text-white text-center lg:text-left px-6 sm:px-10 lg:pl-16 lg:pr-10 xl:pl-24"
                >
                    <motion.h1 variants={fadeInUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3 lg:mb-6">
                        WELCOME <br /> <span className="font-light">TO </span>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-200">EASYMARG</span>
                    </motion.h1>
                    <motion.p variants={fadeInUp} className="text-sm sm:text-lg font-light text-blue-100/90 max-w-lg mx-auto lg:mx-0">
                        Simplify your financial management with our innovative and secure platform.
                    </motion.p>
                </motion.div>
            </div>

            {/* Right Form Panel */}
            <div className="flex-1 min-h-0 flex flex-col items-center bg-gray-50 p-4 sm:p-8 lg:p-12">
                <div className="lg:hidden mb-6 mt-4 bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100">
                    <img src={logo} alt="EasyMarg Logo" className="h-7" />
                </div>
                
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="w-full max-w-[480px] max-h-full my-auto flex flex-col min-h-0"
                >
                    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-3 px-1 shrink-0">
                        <span>EasyMarg</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-[#134DA7] font-semibold">Sign In</span>
                    </div>
                    <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col min-h-0">
                    <div className="h-1.5 bg-gradient-to-r from-[#134DA7] to-[#66BBFC] shrink-0"></div>
                    <div className="auth-scrollbar flex-1 min-h-0 overflow-y-auto p-6 sm:p-8">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-[#0a2463]">Welcome Back</h2>
                            <p className="text-sm text-gray-500 mt-1">Sign in to your account to continue</p>
                        </div>

                        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm text-center border border-red-100">{error}</div>}
                        {successMessage && <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm text-center border border-green-100">{successMessage}</div>}

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase ml-1">Email</label>
                                <div className="relative mt-1 group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FaUser className="text-gray-400 transition-colors group-focus-within:text-[#134DA7]" />
                                    </div>
                                    <input 
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                        placeholder="name@example.com"
                                        className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#134DA7] outline-none transition-colors"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase ml-1">Password</label>
                                <div className="relative mt-1 group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FaLock className="text-gray-400 transition-colors group-focus-within:text-[#134DA7]" />
                                    </div>
                                    <input 
                                        type={showPassword ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="w-full pl-11 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#134DA7] outline-none transition-colors"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#134DA7] transition-colors"
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-600">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="rounded border-gray-300 accent-[#134DA7] focus:ring-0"
                                    />
                                    Remember me
                                </label>
                                <button
                                    type="button"
                                    onClick={() => navigate("/forgot-password", { replace: true })}
                                    className="font-bold text-[#134DA7] hover:underline"
                                >Forgot Password?</button>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-2.5 mt-6 text-white font-bold tracking-wide rounded-xl bg-[#134DA7] hover:bg-[#0f3d85] shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-50"
                            >
                                {loading ? "Signing in..." : "Sign In"}
                            </button>
                        </form>

                        {/* Google Login Section */}
                        <div className="mt-6">
                            <div className="relative flex items-center justify-center mb-6">
                                <div className="flex-grow border-t border-gray-200"></div>
                                <span className="mx-4 text-gray-400 text-xs font-bold uppercase tracking-widest">OR</span>
                                <div className="flex-grow border-t border-gray-200"></div>
                            </div>

                            <div className="flex justify-center w-full">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => setError("Google Login Failed")}
                                    theme="outline"
                                    shape="pill"
                                    width="350"
                                />
                            </div>
                        </div>

                        <div className="mt-6 text-center text-[14px]">
                            <p className="text-gray-600 font-medium">Don’t have an account? <Link to="/signup" className="text-[#134DA7] font-bold underline hover:text-[#0f3d85]">Register</Link></p>
                        </div>
                    </div>
                    </div>
                </motion.div>
            </div>

            {/* Google Login OTP Verification Modal */}
            <AnimatePresence>
                {showOtpModal && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    >
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-3xl shadow-2xl p-6 sm:p-8 w-full max-w-md overflow-hidden text-gray-800"
                        >
                            <button
                                onClick={() => { setShowOtpModal(false); setError(""); }}
                                className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                            >
                                <FaTimes size={18} />
                            </button>

                            <div className="text-center mb-6">
                                <div className="w-16 h-16 bg-blue-50 text-[#134DA7] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                                    <FaShieldAlt size={32} />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">Verify Google Login</h3>
                                <p className="text-sm text-gray-500">
                                    A 6-digit verification code was sent to
                                </p>
                                <p className="text-sm font-semibold text-[#134DA7] mt-0.5 break-all">
                                    {pendingGoogleEmail}
                                </p>
                            </div>

                            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm text-center border border-red-100">{error}</div>}

                            <form onSubmit={handleVerifyGoogleOtp} className="space-y-5">
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider block mb-2 text-center">
                                        Enter 6-Digit Code
                                    </label>
                                    <input
                                        type="text"
                                        maxLength="6"
                                        value={otp}
                                        autoFocus
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        placeholder="• • • • • •"
                                        className="w-full text-center tracking-[12px] text-2xl font-bold py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none focus:border-[#134DA7] focus:bg-white transition-all text-gray-900"
                                    />
                                </div>

                                <div className="flex items-center justify-between text-xs font-medium text-gray-500 px-1">
                                    <span>
                                        {countdown > 0 ? (
                                            <span>Code expires in <strong className="text-[#134DA7]">{countdown}s</strong></span>
                                        ) : (
                                            <span className="text-amber-600">Code expired?</span>
                                        )}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={!canResend || resendLoading}
                                        onClick={handleResendGoogleOtp}
                                        className={`flex items-center gap-1 font-bold transition-colors ${
                                            canResend && !resendLoading 
                                                ? "text-[#134DA7] hover:underline cursor-pointer" 
                                                : "text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        <FaRedo size={11} className={resendLoading ? "animate-spin" : ""} />
                                        <span>{resendLoading ? "Sending..." : "Resend Code"}</span>
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    disabled={otpLoading || otp.length !== 6}
                                    className="w-full py-3.5 text-white font-bold tracking-wide text-base bg-[#134DA7] hover:bg-[#0f3d85] rounded-xl shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {otpLoading ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Verifying...</span>
                                        </>
                                    ) : (
                                        "Verify & Sign In"
                                    )}
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Login;